import { useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import "./ResearchGap.css";

const scrollToReportReference = (refNum) => {
    const el = document.getElementById(`paper-${refNum}`);
    if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
};

function formatSourceLabel(article, refNum) {
    const rawAuthors = article?.authors;
    const authorsList = Array.isArray(rawAuthors) ? rawAuthors : (rawAuthors ? [rawAuthors] : []);
    const firstAuthor = authorsList[0] ?? "";

    let lastName = "";
    if (firstAuthor.includes(",")) {
        lastName = firstAuthor.split(",")[0].trim();
    } else {
        const parts = firstAuthor.trim().split(/\s+/);
        if (parts.length >= 2) {
            const last = parts[parts.length - 1];
            if (last.length === 1 || (last.length === 2 && last[1] === ".")) {
                lastName = parts.slice(0, -1).join(" ");
            } else {
                lastName = last;
            }
        } else {
            lastName = firstAuthor;
        }
    }

    const year = article?.publicationDate
        ? String(article.publicationDate).match(/\d{4}/)?.[0] ?? ""
        : "";
    const suffix = authorsList.length > 1 ? " et al." : "";
    const name = lastName ? `${lastName}${suffix}` : "";
    if (name && year) return `${name}, ${year}`;
    if (name) return name;
    if (year) return String(year);
    return article?.title ? String(article.title).slice(0, 60) : `Study ${refNum}`;
}

const CitationLink = ({ refNum, label }) => (
    <a
        href={`#paper-${refNum}`}
        className="researchgap-citation-link"
        onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            scrollToReportReference(refNum);
        }}
    >
        {label ?? `[${refNum}]`}
    </a>
);

/** Turn plain text with [n] markers into nodes with clickable citation links. */
const linkifyCitations = (text) => {
    if (!text) return text;
    const parts = String(text).split(/(\[\d+\])/g);
    return parts.map((part, i) => {
        const match = part.match(/^\[(\d+)\]$/);
        if (!match) return part;
        return <CitationLink key={`${match[1]}-${i}`} refNum={match[1]} />;
    });
};

const ResearchGap = ({ researchGap, isLoading, error, hasGenerated, onGenerate, articles = [] }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);

    const toggleCollapse = () => setIsCollapsed((prev) => !prev);

    const evidenceCoverage = Array.isArray(researchGap?.EvidenceCoverage)
        ? researchGap.EvidenceCoverage
        : [];

    const getArticleForRef = (refNum) => {
        const index = Number(refNum) - 1;
        if (!Number.isFinite(index) || index < 0) return null;
        return articles[index] ?? null;
    };

    return (
        <div className="researchgap-section">
            <div className="researchgap-header" onClick={toggleCollapse}>
                <span className="researchgap-title">Research Gap Analysis</span>
                {hasGenerated && (
                    <span className="researchgap-toggle">{isCollapsed ? "▼" : "▲"}</span>
                )}
            </div>

            {!isCollapsed && (
                <div className="researchgap-body">
                    {!hasGenerated && !isLoading && (
                        <div className="researchgap-intro">
                            <p>
                                Break down the evidence in this review by type, and check
                                whether it's enough to fully answer the research question —
                                or where a gap remains.
                            </p>
                            <button
                                type="button"
                                className="researchgap-generate-button"
                                onClick={onGenerate}
                            >
                                Generate Research Gap Analysis
                            </button>
                        </div>
                    )}

                    {isLoading && (
                        <div className="researchgap-loading">
                            <CircularProgress sx={{ color: "#0d9488" }} size={24} />
                            <span>Analyzing evidence gaps…</span>
                        </div>
                    )}

                    {!isLoading && error && (
                        <div className="researchgap-error">
                            {error}
                            <button
                                type="button"
                                className="researchgap-generate-button"
                                onClick={onGenerate}
                            >
                                Try Again
                            </button>
                        </div>
                    )}

                    {!isLoading && !error && researchGap && (
                        <div className="researchgap-results">
                            {evidenceCoverage.length > 0 && (
                                <div className="researchgap-evidence-list">
                                    {evidenceCoverage.map((evidence, index) => (
                                        <div className="researchgap-evidence-item" key={index}>
                                            <div className="researchgap-evidence-label">
                                                {evidence.Label}
                                            </div>
                                            <p className="researchgap-evidence-summary">
                                                {linkifyCitations(evidence.Summary)}
                                            </p>
                                            {Array.isArray(evidence.KeyQuantitativeFindings) &&
                                                evidence.KeyQuantitativeFindings.length > 0 && (
                                                    <ul className="researchgap-evidence-findings">
                                                        {evidence.KeyQuantitativeFindings.map((finding, i) => (
                                                            <li key={i}>{linkifyCitations(finding)}</li>
                                                        ))}
                                                    </ul>
                                                )}
                                            {Array.isArray(evidence.StudyReferences) &&
                                                evidence.StudyReferences.length > 0 && (
                                                    <div className="researchgap-sources">
                                                        <div className="researchgap-sources-label">
                                                            Sources analyzed
                                                        </div>
                                                        <ul className="researchgap-sources-list">
                                                            {evidence.StudyReferences.map((ref, refIndex) => {
                                                                const article = getArticleForRef(ref);
                                                                const label = formatSourceLabel(article, ref);
                                                                return (
                                                                    <li key={`${ref}-${refIndex}`}>
                                                                        <CitationLink refNum={ref} label={label} />
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </div>
                                                )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {researchGap.ConflationNote && (
                                <div className="researchgap-conflation-note">
                                    {linkifyCitations(researchGap.ConflationNote)}
                                </div>
                            )}

                            {researchGap.GapStatement ? (
                                <div className="researchgap-gap-statement">
                                    <div className="researchgap-gap-statement-label">The Gap</div>
                                    <p>{linkifyCitations(researchGap.GapStatement)}</p>
                                </div>
                            ) : (
                                <div className="researchgap-gap-statement researchgap-no-gap">
                                    <div className="researchgap-gap-statement-label">The Gap</div>
                                    <p>No significant evidence gap identified — the retrieved studies collectively provide direct evidence for this question.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ResearchGap;
